/**
 * Discord Share Service
 *
 * Frontend service to call the share-to-discord API endpoint with proper authentication.
 * Implementation follows L4-DISCORD-006 specification.
 */

import { auth } from '../config/firebase';

/**
 * Result type for share operations
 */
export interface ShareResult {
  success: boolean;
  error?: 'ALREADY_SHARED' | 'NOT_OWNER' | 'IMAGE_FAILED' | 'DISCORD_ERROR' | 'NETWORK_ERROR' | 'AUTH_ERROR';
  message: string;
}

/**
 * Discord Share Service class
 *
 * Handles sharing creatures to Discord via the backend API.
 */
class DiscordShareService {
  private baseUrl: string;

  constructor() {
    // Use VITE_API_URL from environment; fall back to empty string for relative URLs
    this.baseUrl = import.meta.env.VITE_API_URL || '';
  }

  /**
   * Share a creature to Discord
   *
   * @param creatureId - ID of the creature to share
   * @returns Promise<ShareResult> - Result of the share operation
   */
  async shareCreature(creatureId: string): Promise<ShareResult> {
    // Check if user is authenticated
    const user = auth.currentUser;
    if (!user) {
      return {
        success: false,
        error: 'AUTH_ERROR',
        message: 'Please sign in to share'
      };
    }

    try {
      // Get Firebase ID token
      const token = await user.getIdToken();

      // Make API request
      const response = await fetch(
        `${this.baseUrl}/api/creature-library/${creatureId}/share-discord`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Parse response
      const data = await response.json();
      return data;
    } catch (error) {
      // Handle network errors and other exceptions
      return {
        success: false,
        error: 'NETWORK_ERROR',
        message: 'Connection failed. Please try again.'
      };
    }
  }
}

// Export singleton instance
export const discordShareService = new DiscordShareService();
